/**
 * Branding white-label — único sítio com o nome/logo da marca no frontend.
 *
 * Os valores vêm de build args Vite (VITE_APP_NAME, VITE_APP_LOGO,
 * VITE_APP_FAVICON) com fallback para a marca Afisocks. A build da demo
 * (AI-HumanERP) passa estes args no Dockerfile/workflow — NUNCA escrever
 * "Afisocks" hardcoded em componentes; importar daqui.
 */
export const APP_NAME: string = import.meta.env.VITE_APP_NAME || "ERP";
/**
 * Logótipo da marca.
 *
 * O fallback é VAZIO e não o logo de uma marca concreta. O `brand.json` é
 * injectado em toda a build (ver `brandEnv()` no vite.config), por isso este
 * valor só aparece se ele faltar — e aí é preferível não mostrar logótipo
 * nenhum a mostrar o de outro cliente. Quem desenha trata o vazio: o cabeçalho
 * mostra o nome, que nunca é vazio.
 *
 * Isto não é hipotético: a EPEL esteve em produção a pedir
 * `/brands/afisocks/logo.png` (404), porque o `ENV VITE_APP_LOGO=""` do
 * Dockerfile ganhava ao `brand.json` e caía aqui.
 */
export const APP_LOGO: string = import.meta.env.VITE_APP_LOGO || "";
export const APP_TAGLINE = "Sistema de Gestão Empresarial";

/**
 * O id da marca desta build (`afisocks`, `epel`, ...).
 *
 * Serve para montar caminhos dentro de `/brands/<id>/` — nunca para decidir
 * comportamento. Um `if (APP_BRAND_ID === "x")` é a mesma coisa que
 * `if (cliente === "X")`, e a resposta é a mesma: ou é uma feature no perfil,
 * ou é configuração que devia vir do servidor.
 */
export const APP_BRAND_ID: string = import.meta.env.VITE_APP_BRAND || "";

/** Caminho de um ficheiro da marca activa: `brandAsset("templates/x.png")`. */
export function brandAsset(caminho: string): string {
  return APP_BRAND_ID ? `/brands/${APP_BRAND_ID}/${caminho}` : "";
}

/**
 * Imagem do painel do ecrã de entrada, por marca.
 *
 * Vazia = o painel usa o desenho em CSS (a cor da marca), que é o que acontece
 * enquanto a marca não tiver imagem própria — nunca fica um espaço partido.
 */
export const APP_AUTH_IMAGE: string = import.meta.env.VITE_APP_AUTH_IMAGE || "";

/**
 * Vídeo do painel do ecrã de entrada, por marca — opcional.
 *
 * Quando definido, substitui `APP_AUTH_IMAGE` como fundo do painel; a imagem
 * continua a servir de poster (visível antes do vídeo carregar, e se o
 * `prefers-reduced-motion` do visitante pedir sem movimento).
 */
export const APP_AUTH_VIDEO: string = import.meta.env.VITE_APP_AUTH_VIDEO || "";

/**
 * A frase do painel de entrada. Pertence à marca, não à plataforma: a da
 * Afisocks é a da própria empresa ("Tecnologia de ponta. As melhores soluções." —
 * afisocks.com); a demo tem a sua. Sem isto, uma build white-label falaria de
 * máquinas e turnos de uma fábrica de meias que não é a dela.
 */
export const APP_AUTH_EYEBROW: string = import.meta.env.VITE_APP_AUTH_EYEBROW || "";
export const APP_AUTH_HEADLINE: string = import.meta.env.VITE_APP_AUTH_HEADLINE || "";
export const APP_AUTH_SUBHEAD: string = import.meta.env.VITE_APP_AUTH_SUBHEAD || "";
/**
 * Nome legal e frase de apresentação das páginas públicas de recrutamento.
 *
 * A entidade que contrata não é a marca do produto: quem se candidata assina
 * com "A. Fiúza & Irmão", não com "Afisocks". Estavam escritos à mão em três
 * páginas — incluindo o rodapé de direitos reservados, que numa build de outro
 * cliente atribuía a empresa errada.
 *
 * As páginas só existem em quem tem a feature `hr.recrutamento_publico`, mas o
 * fallback fica na mesma: um cliente que a compre e não defina o nome legal vê
 * o nome da marca, nunca o de outra empresa.
 */
export const APP_LEGAL_NAME: string = import.meta.env.VITE_APP_LEGAL_NAME || APP_NAME;
export const APP_CAREERS_TAGLINE: string = import.meta.env.VITE_APP_CAREERS_TAGLINE || "";
